import { useCallback, useEffect, useState } from 'react';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { Provider } from 'react-redux';
import { store } from './store/store';
import { MainLayout } from './components/Layout/MainLayout';
import { LoginPage } from './components/Login/LoginPage';
import { ErrorBoundary } from './components/Common/ErrorBoundary';
import { TutorialGuide } from './components/Tutorial/TutorialGuide';
import { GeofenceAlerts } from './components/Fleet/GeofenceAlerts';
import { BatteryLandingAlerts } from './components/Fleet/BatteryLandingAlerts';
import { CredentialsRevokedAlerts } from './components/Fleet/CredentialsRevokedAlerts';
import { userManager } from './auth/oidcConfig';
import { useTheme } from './hooks/useTheme';
import { useWebSocket } from './hooks/useWebSocket';
import { useDroneData } from './hooks/useDroneData';
import { useCurrentUser } from './hooks/useCurrentUser';
import { lightTheme, darkTheme } from './styles/themes';
import './styles/globals.css';

/**
 * The authenticated dashboard. The real-time WebSocket, initial data fetch and
 * account load live here (not in AppContent) so they only run once the operator
 * is signed in — otherwise every load fires unauthenticated API/WS calls that
 * 401.
 */
const Dashboard: React.FC = () => {
  useWebSocket();
  useDroneData();
  // Loads the account and applies the stored theme (#378). Here rather than in
  // the settings screen: a theme saved on another device must take effect on
  // load, not only once the user visits the page that saves it.
  useCurrentUser();
  return (
    <>
      <MainLayout />
      {/* Guided onboarding for tours this operator has not seen (#427). */}
      <TutorialGuide />
      {/* Fleet-wide safety alerts, above whichever view is open (#452). */}
      <GeofenceAlerts />
      {/* An aircraft putting itself down on a low battery, and where (#629). Its
          own surface rather than a case inside GeofenceAlerts: the two are
          different events with different actions, and one component deciding
          which to render is how the wording of both ends up compromised. */}
      <BatteryLandingAlerts />
      {/* A drone cut off for a reused refresh token (#777): it cannot say so. */}
      <CredentialsRevokedAlerts />
    </>
  );
};

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

const AppContent: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [status, setStatus] = useState<AuthStatus>('loading');
  // The OIDC provider redirects back to /callback with the auth code.
  const isCallback = window.location.pathname === '/callback';

  useEffect(() => {
    // On the callback path, LoginPage completes the code exchange itself.
    if (isCallback) return;
    let active = true;
    userManager
      .getUser()
      .then((user) => {
        if (active) setStatus(user && !user.expired ? 'authenticated' : 'anonymous');
      })
      .catch(() => active && setStatus('anonymous'));
    return () => {
      active = false;
    };
  }, [isCallback]);

  // The session can end while the app is open: the API returns 401 and clears
  // it, silent renew fails, or the operator signs out. Any of those unloads the
  // OIDC user — drop straight back to the login gate rather than leaving a
  // dashboard that silently fails every request.
  useEffect(() => {
    const onSessionLost = () => setStatus('anonymous');
    // Expiry only ends the session once the dead token is dropped, which emits
    // `userUnloaded` — keeping one path into the gate.
    const onTokenExpired = () => void userManager.removeUser().catch(() => undefined);
    userManager.events.addUserUnloaded(onSessionLost);
    userManager.events.addAccessTokenExpired(onTokenExpired);
    return () => {
      userManager.events.removeUserUnloaded(onSessionLost);
      userManager.events.removeAccessTokenExpired(onTokenExpired);
    };
  }, []);

  const onLoginSuccess = useCallback(() => {
    // Drop the /callback path from the URL, then enter the app.
    window.history.replaceState({}, '', '/');
    setStatus('authenticated');
  }, []);

  let content;
  if (isCallback || status === 'anonymous') {
    content = <LoginPage onLoginSuccess={onLoginSuccess} />;
  } else if (status === 'loading') {
    content = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  } else {
    content = <Dashboard />;
  }

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
